import type { ManagementExplorerSliceRow } from "../types/managementExplorer";
import type { PolicyTreeV2, PolicyTreeV2Rule } from "../types/tradePolicy";
import { formatSegmentKeyDisplay } from "./managementActionLabels";
import {
  exitPlacementSliceDisplayLabel,
  formatSituation,
} from "./exitPlacementDisplay";
import {
  buildExplorerSliceId,
  isPolicyTreeV2Rule,
} from "./managementExplorer";
import { sliceCanAddToCart } from "./exitPlacementDisplay";
import {
  buildPickId,
  type ReportPick,
  type SimulatorPickOrgLane,
} from "./reportPickListStorage";
import { pickQuickStatsFromMetrics, quickStatsMetaPatch } from "./reportPickQuickStats";

export function isExitPlacementPick(pick: ReportPick): boolean {
  return pick.kind === "exit_placement";
}

export function exitPlacementPicksForReport(picks: ReportPick[]): ReportPick[] {
  return picks.filter(isExitPlacementPick);
}

export function pickedExitPlacementSliceIds(picks: ReportPick[]): Set<string> {
  const out = new Set<string>();
  for (const pick of exitPlacementPicksForReport(picks)) {
    const id =
      String(pick.sliceId ?? "").trim() ||
      String(pick.meta?.explorer_slice_id ?? "").trim();
    if (id) out.add(id);
  }
  return out;
}

export function findExitPlacementPickForSlice(
  picks: ReportPick[],
  sliceId: string
): ReportPick | null {
  const target = sliceId.trim();
  if (!target) return null;
  for (const pick of exitPlacementPicksForReport(picks)) {
    const id =
      String(pick.sliceId ?? "").trim() ||
      String(pick.meta?.explorer_slice_id ?? "").trim();
    if (id === target) return pick;
  }
  return null;
}

/** Find simulator pick backing a policy_tree.v2 rule (by simulation_rule.id). */
export function findExitPlacementPickForRuleId(
  picks: ReportPick[],
  ruleId: string
): ReportPick | null {
  const target = ruleId.trim();
  if (!target) return null;
  for (const pick of exitPlacementPicksForReport(picks)) {
    const rule = simulationRuleFromExitPlacementPick(pick);
    if (rule?.id?.trim() === target) return pick;
  }
  return null;
}

export function buildExitPlacementPick(
  row: ManagementExplorerSliceRow,
  reportId: string,
  simulatorLane: SimulatorPickOrgLane = "single"
): ReportPick | null {
  if (!sliceCanAddToCart(row)) return null;
  const sliceId = buildExplorerSliceId(row);
  if (!sliceId) return null;
  const label = exitPlacementSliceDisplayLabel(row);
  const meta: Record<string, unknown> = {
    explorer_slice_id: sliceId,
    segment_key: row.segment_key ?? "",
    segment_label: row.segment_label ?? "",
    display_label: label,
    ...(row.situation?.trim() ? { situation: row.situation.trim() } : {}),
    simulation_rule: row.simulation_rule,
    ...(row.exit_placement?.display ? { exit_placement_display: row.exit_placement.display } : {}),
    ...(row.exit_placement?.engine ? { exit_placement_engine: row.exit_placement.engine } : {}),
    ...quickStatsMetaPatch(
      pickQuickStatsFromMetrics({
        sample_size: row.sample_size,
        total_pnl: row.total_pnl,
        expectancy: row.expectancy,
        win_rate: row.win_rate,
      })
    ),
  };
  return {
    v: 1,
    id: buildPickId("exit_placement", label, meta, reportId),
    createdAtMs: Date.now(),
    kind: "exit_placement",
    label,
    reportId: reportId.trim(),
    sliceId,
    simulatorLane: simulatorLane === "combined" ? "combined" : "single",
    meta,
  };
}

export function sliceIdFromExitPlacementPick(pick: ReportPick): string | null {
  const id =
    String(pick.sliceId ?? "").trim() ||
    String(pick.meta?.explorer_slice_id ?? "").trim() ||
    String(pick.meta?.slice_id ?? "").trim();
  return id || null;
}

export function sliceIdsFromExitPlacementPicks(picks: ReportPick[]): string[] {
  const ids = new Set<string>();
  for (const pick of exitPlacementPicksForReport(picks)) {
    const id = sliceIdFromExitPlacementPick(pick);
    if (id) ids.add(id);
  }
  return Array.from(ids);
}

export function simulationRuleFromExitPlacementPick(pick: ReportPick): PolicyTreeV2Rule | null {
  if (!isExitPlacementPick(pick)) return null;
  const raw = pick.meta?.simulation_rule;
  return isPolicyTreeV2Rule(raw) ? raw : null;
}

export function buildPolicyTreeFromExitPlacementPicks(picks: ReportPick[]): PolicyTreeV2 | null {
  const rules: PolicyTreeV2Rule[] = [];
  for (const pick of exitPlacementPicksForReport(picks)) {
    const rule = simulationRuleFromExitPlacementPick(pick);
    if (rule) rules.push(rule);
  }
  if (!rules.length) return null;
  return { schema_version: "policy_tree.v2", rules };
}

/** Human-readable label for an exit-placement pick (stored label with legacy meta fallback). */
export function exitPlacementPickDisplayLabel(pick: ReportPick): string {
  const fromMeta =
    typeof pick.meta?.display_label === "string" ? pick.meta.display_label.trim() : "";
  if (fromMeta) return fromMeta;

  const fromLabel = pick.label?.trim();
  if (fromLabel) return fromLabel;

  const bucket =
    (typeof pick.meta?.segment_label === "string" && pick.meta.segment_label.trim()) ||
    formatSegmentKeyDisplay(String(pick.meta?.segment_key ?? "")) ||
    "";
  const situation = typeof pick.meta?.situation === "string" ? pick.meta.situation.trim() : "";
  if (situation) {
    const formatted = formatSituation(situation);
    if (bucket && formatted) return `${bucket} · ${formatted}`;
    return formatted || bucket;
  }
  return bucket || sliceIdFromExitPlacementPick(pick) || "Context";
}

/** Bucket-only label from pick meta (when situation should be omitted). */
export function exitPlacementPickSegmentLabel(pick: ReportPick): string {
  const fromMeta =
    typeof pick.meta?.segment_label === "string" ? pick.meta.segment_label.trim() : "";
  if (fromMeta) return fromMeta;
  return formatSegmentKeyDisplay(String(pick.meta?.segment_key ?? "")) || "";
}

/** Human-readable rule titles keyed by simulation_rule.id (from explorer picks). */
export function exitPlacementRuleLabelsFromPicks(picks: ReportPick[]): Map<string, string> {
  const out = new Map<string, string>();
  for (const pick of exitPlacementPicksForReport(picks)) {
    const rule = simulationRuleFromExitPlacementPick(pick);
    const ruleId = rule?.id?.trim();
    if (!ruleId) continue;
    const label = exitPlacementPickDisplayLabel(pick);
    if (label) out.set(ruleId, label);
  }
  return out;
}

export function segmentKeysFromExitPlacementPicks(picks: ReportPick[]): string[] {
  const keys = new Set<string>();
  for (const pick of exitPlacementPicksForReport(picks)) {
    const key = String(pick.meta?.segment_key ?? "").trim();
    if (key) keys.add(key);
  }
  return Array.from(keys);
}
